/**
 * AI Insights Panel - ML-powered treasury intelligence
 * Volvo-Inspired Design System V2
 */
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Sparkles,
  Brain,
  TrendingUp,
  AlertCircle,
  RefreshCw,
  ChevronRight,
  Zap,
  Target,
  ShieldCheck,
  Cpu,
  Lock,
} from 'lucide-react';
import { Button, Spinner } from '@/components/ui-v2';
import {
  useMLStatus,
  useMLHealthScore,
  useGenerateMLForecasts,
  useGenerateMLRecommendations,
} from '../hooks/useTreasury';
import { notifications } from '@/lib/notifications';

interface AIInsightsPanelProps {
  companyId: string;
}

export function AIInsightsPanel({ companyId }: AIInsightsPanelProps) {
  const { t } = useTranslation();
  const [isGenerating, setIsGenerating] = useState(false);
  const [featureUnavailable, setFeatureUnavailable] = useState(false);

  const {
    data: mlStatus,
    isLoading: statusLoading,
    isError: statusError,
    error: statusErrorData
  } = useMLStatus(companyId);
  const {
    data: mlHealthScore,
    isLoading: healthLoading,
    isError: healthError,
    error: healthErrorData,
    refetch: refetchHealth
  } = useMLHealthScore(companyId);
  const generateForecasts = useGenerateMLForecasts();
  const generateRecommendations = useGenerateMLRecommendations();

  // Handle 403 errors - feature not available for account
  useEffect(() => {
    const is403Error = (error: unknown): boolean => {
      if (error instanceof Error) {
        const message = error.message.toLowerCase();
        return message.includes('403') ||
               message.includes('fonctionnalité n\'est pas') ||
               message.includes('pas encore disponible');
      }
      return false;
    };

    if (statusError || healthError) {
      const error = statusErrorData || healthErrorData;

      if (is403Error(error)) {
        // Feature not available - don't show error notification, just set state
        setFeatureUnavailable(true);
      } else {
        // Other errors - show notification
        notifications.loadError(t('treasury.ai.mlDataError', 'les données ML de trésorerie'));
      }
    }
  }, [statusError, healthError, statusErrorData, healthErrorData]);

  const handleGenerateInsights = async () => {
    setIsGenerating(true);
    try {
      await Promise.all([
        generateForecasts.mutateAsync({ companyId, daysAhead: 90 }),
        generateRecommendations.mutateAsync(companyId),
      ]);
      await refetchHealth();
      notifications.success(t('treasury.ai.insightsGenerated', 'Insights ML générés avec succès'));
    } catch (error: any) {
      notifications.error(error.message || t('treasury.ai.generationError', 'Erreur lors de la génération'));
    } finally {
      setIsGenerating(false);
    }
  };

  const cardClasses = `bg-white/20 backdrop-blur-xl rounded-3xl shadow-xl shadow-gray-300/10
    border border-white/30 p-6`;

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-teal-600';
    if (score >= 60) return 'text-yellow-600';
    if (score >= 40) return 'text-orange-600';
    return 'text-red-600';
  };

  const getScoreBg = (score: number) => {
    if (score >= 80) return 'bg-teal-100';
    if (score >= 60) return 'bg-yellow-100';
    if (score >= 40) return 'bg-orange-100';
    return 'bg-red-100';
  };

  // Show loading state
  if (statusLoading && !featureUnavailable) {
    return (
      <div className={cardClasses}>
        <div className="flex items-center justify-center py-8">
          <Spinner size="lg" />
        </div>
      </div>
    );
  }

  // Show feature unavailable state (403 error)
  if (featureUnavailable || statusError) {
    return (
      <div className={cardClasses}>
        <div className="text-center py-8">
          <div className="p-3 bg-gray-100 rounded-2xl w-fit mx-auto mb-4">
            <Lock className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {t('treasury.ai.title', 'Intelligence IA')}
          </h3>
          <p className="text-gray-500 text-sm mb-4">
            {t('treasury.ai.unavailable', 'Cette fonctionnalité n\'est pas encore disponible pour votre compte.')}
          </p>
          <p className="text-gray-400 text-xs">
            {t('treasury.ai.contactAdmin', 'Contactez votre administrateur pour activer les fonctionnalités ML avancées.')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={cardClasses}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{t('treasury.ai.title')}</h3>
            <p className="text-xs text-gray-500">
              {mlStatus?.openaiEnabled ? t('treasury.ai.poweredByGpt4') : t('treasury.ai.heuristicMode')}
            </p>
          </div>
        </div>
        <Button
          onClick={handleGenerateInsights}
          loading={isGenerating}
          variant="ghost"
          size="sm"
          icon={<RefreshCw className="w-4 h-4" />}
        >
          {t('treasury.ai.refresh')}
        </Button>
      </div>

      {/* Status Badge */}
      <div className="flex items-center gap-2 mb-6">
        <div className={`px-3 py-1.5 rounded-full flex items-center gap-2 ${
          mlStatus?.openaiEnabled
            ? 'bg-purple-100 text-purple-700'
            : 'bg-gray-100 text-gray-600'
        }`}>
          <Cpu className="w-3.5 h-3.5" />
          <span className="text-xs font-medium">
            {mlStatus?.modelVersion || 'heuristics-v1'}
          </span>
        </div>
        {mlStatus?.aiRecommendationsEnabled && (
          <div className="px-3 py-1.5 rounded-full bg-teal-100 text-teal-700 flex items-center gap-2">
            <ShieldCheck className="w-3.5 h-3.5" />
            <span className="text-xs font-medium">{t('treasury.ai.active')}</span>
          </div>
        )}
      </div>

      {/* Health Score */}
      {healthLoading ? (
        <div className="flex justify-center py-4">
          <Spinner size="md" />
        </div>
      ) : mlHealthScore ? (
        <div className="space-y-4">
          {/* Overall Score */}
          <div className="p-4 bg-white/40 rounded-2xl">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium text-gray-700">{t('treasury.ai.overallScore')}</span>
              <div className={`flex items-center gap-1 ${
                mlHealthScore.trend === 'UP' ? 'text-teal-600' :
                mlHealthScore.trend === 'DOWN' ? 'text-red-600' : 'text-gray-500'
              }`}>
                <TrendingUp className={`w-4 h-4 ${mlHealthScore.trend === 'DOWN' ? 'rotate-180' : ''}`} />
                <span className="text-xs font-medium">{mlHealthScore.trend}</span>
              </div>
            </div>
            <div className="flex items-end gap-2">
              <span className={`text-4xl font-bold ${getScoreColor(mlHealthScore.overallScore)}`}>
                {mlHealthScore.overallScore}
              </span>
              <span className="text-lg text-gray-400 mb-1">/100</span>
            </div>
            {/* Score Bar */}
            <div className="mt-3 h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-500 ${getScoreBg(mlHealthScore.overallScore).replace('bg-', 'bg-').replace('-100', '-500')}`}
                style={{ width: `${mlHealthScore.overallScore}%` }}
              />
            </div>
          </div>

          {/* Component Scores */}
          {mlHealthScore.components && (
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 bg-white/30 rounded-xl">
                <div className="flex items-center gap-2 mb-1">
                  <Zap className="w-4 h-4 text-blue-500" />
                  <span className="text-xs text-gray-600">{t('treasury.ai.liquidity')}</span>
                </div>
                <span className={`text-lg font-semibold ${getScoreColor(mlHealthScore.components.liquidity)}`}>
                  {mlHealthScore.components.liquidity}%
                </span>
              </div>
              <div className="p-3 bg-white/30 rounded-xl">
                <div className="flex items-center gap-2 mb-1">
                  <TrendingUp className="w-4 h-4 text-teal-500" />
                  <span className="text-xs text-gray-600">{t('treasury.ai.cashFlow')}</span>
                </div>
                <span className={`text-lg font-semibold ${getScoreColor(mlHealthScore.components.cashFlow)}`}>
                  {mlHealthScore.components.cashFlow}%
                </span>
              </div>
              <div className="p-3 bg-white/30 rounded-xl">
                <div className="flex items-center gap-2 mb-1">
                  <Target className="w-4 h-4 text-purple-500" />
                  <span className="text-xs text-gray-600">{t('treasury.ai.collection')}</span>
                </div>
                <span className={`text-lg font-semibold ${getScoreColor(mlHealthScore.components.collectionEfficiency)}`}>
                  {mlHealthScore.components.collectionEfficiency}%
                </span>
              </div>
              <div className="p-3 bg-white/30 rounded-xl">
                <div className="flex items-center gap-2 mb-1">
                  <ShieldCheck className="w-4 h-4 text-orange-500" />
                  <span className="text-xs text-gray-600">{t('treasury.ai.payments')}</span>
                </div>
                <span className={`text-lg font-semibold ${getScoreColor(mlHealthScore.components.paymentBehavior)}`}>
                  {mlHealthScore.components.paymentBehavior}%
                </span>
              </div>
            </div>
          )}

          {/* AI Insights */}
          {mlHealthScore.insights && mlHealthScore.insights.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <Brain className="w-4 h-4 text-purple-500" />
                {t('treasury.ai.insightsTitle')}
              </h4>
              {mlHealthScore.insights.slice(0, 3).map((insight, i) => (
                <div
                  key={i}
                  className="p-3 bg-purple-50/50 border border-purple-100 rounded-xl text-sm text-gray-700 flex items-start gap-2"
                >
                  <Sparkles className="w-4 h-4 text-purple-500 mt-0.5 flex-shrink-0" />
                  <span>{insight}</span>
                </div>
              ))}
            </div>
          )}

          {/* Recommendations */}
          {mlHealthScore.recommendations && mlHealthScore.recommendations.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-gray-700 flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-amber-500" />
                {t('treasury.ai.recommendationsTitle')}
              </h4>
              {mlHealthScore.recommendations.slice(0, 2).map((rec, i) => (
                <div
                  key={i}
                  className="p-3 bg-amber-50/50 border border-amber-100 rounded-xl text-sm text-gray-700 flex items-start gap-2"
                >
                  <ChevronRight className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                  <span>{rec}</span>
                </div>
              ))}
            </div>
          )}

          {/* AI Badge */}
          {mlHealthScore.aiGenerated && (
            <div className="text-center pt-2">
              <span className="inline-flex items-center gap-1 text-xs text-purple-600">
                <Sparkles className="w-3 h-3" />
                {t('treasury.ai.generatedByAI')}
              </span>
            </div>
          )}
        </div>
      ) : (
        <div className="text-center py-6">
          <div className="p-3 bg-purple-100 rounded-2xl w-fit mx-auto mb-3">
            <Brain className="w-8 h-8 text-purple-600" />
          </div>
          <p className="text-gray-600 text-sm">
            {t('treasury.ai.clickToGenerate')}
          </p>
        </div>
      )}

      {/* Stats */}
      {mlStatus && (
        <div className="mt-6 pt-4 border-t border-gray-200/50">
          <div className="flex justify-between text-xs text-gray-500">
            <span>{t('treasury.ai.forecastsCount', { count: mlStatus.totalForecastsGenerated })}</span>
            <span>{t('treasury.ai.recommendationsCount', { count: mlStatus.totalRecommendationsGenerated })}</span>
          </div>
        </div>
      )}
    </div>
  );
}

export default AIInsightsPanel;
